/**
 * Admin Config Service (US-16-GR-23)
 *
 * Reads and writes AI configuration via the admin-gated endpoint.
 * Relative /api paths route through the vite proxy in dev (vite.config.ts) and
 * are same-origin in production, so no base URL is needed.
 *
 * AIConfig is aligned 1-to-1 with AiConfigResponse in AdminConfigController.cs.
 * ASP.NET serialises to camelCase:
 *   ConfidenceThreshold   → confidenceThreshold
 *   DailyEasyButtonQuota  → dailyEasyButtonQuota
 *   BasicDailyQuota       → basicDailyQuota
 *   ProDailyQuota         → proDailyQuota
 *   PremiumDailyQuota     → premiumDailyQuota
 *   EasyButtonModel       → easyButtonModel
 *
 * Story 17437: primaryProvider, fallbackProvider, wizardModel and
 * ghostWritingModel were dropped from both directions of this contract — no
 * AI request path read them. See AiConfigPage.tsx for the full rationale.
 */

import { authenticatedFetch } from '../api/authenticatedFetch';
import { COLDSTART_RETRY_CONFIG } from '../api/resilientFetch';

// ---------------------------------------------------------------------------
// DTO shape — aligned to AiConfigResponse in AdminConfigController.cs
// ---------------------------------------------------------------------------

export interface AIConfig {
  /** Minimum confidence score (0.0–1.0) before AI acts autonomously */
  confidenceThreshold: number;
  /** Maximum number of Easy Button uses per user per day */
  dailyEasyButtonQuota: number;
  /** Basic-tier Easy Button uses per user per day; 0 means no daily limit */
  basicDailyQuota: number;
  /** Pro-tier Easy Button uses per user per day; 0 means no daily limit */
  proDailyQuota: number;
  /** Premium-tier Easy Button uses per user per day; 0 means no daily limit */
  premiumDailyQuota: number;
  /** Model name used for the AI Easy Button */
  easyButtonModel: string;
}

/** One offending field from a 400 model-catalog rejection. */
export interface ModelCatalogRejection {
  /** camelCase JSON name of the offending AIConfig property, e.g. "wizardModel" */
  field: string;
  value: string;
  allowed: string[];
}

/**
 * Thrown by updateAIConfig when PUT /api/admin/config/ai responds 400 because
 * one or more submitted model ids are not in the backend's allowed catalog.
 * Carries the full per-field rejection list so the UI can mark every
 * offending field, not just the first.
 */
export class ModelCatalogRejectionError extends Error {
  readonly rejected: ModelCatalogRejection[];

  constructor(message: string, rejected: ModelCatalogRejection[]) {
    super(message);
    this.name = 'ModelCatalogRejectionError';
    this.rejected = rejected;
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function fetchJson<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await authenticatedFetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  }, { retryConfig: COLDSTART_RETRY_CONFIG });
  if (!response.ok) {
    throw new Error(`Admin config request failed: HTTP ${response.status} ${path}`);
  }
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * GET /api/admin/config/ai — fetch current AI configuration.
 */
export async function getAIConfig(): Promise<AIConfig> {
  return fetchJson<AIConfig>('/api/admin/config/ai');
}

/**
 * PUT /api/admin/config/ai — update AI configuration.
 * The server persists all three fields and writes the Admin.ConfigUpdated Receipt.
 * Returns the freshly-read AiConfigResponse.
 *
 * A 400 response means one or more model ids failed the backend's allowed-catalog
 * check; that body carries a structured `rejected` list instead of the updated
 * config, so it is handled separately from the generic error path in fetchJson.
 */
export async function updateAIConfig(config: AIConfig): Promise<AIConfig> {
  const path = '/api/admin/config/ai';
  const response = await authenticatedFetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  }, { retryConfig: COLDSTART_RETRY_CONFIG });

  if (response.status === 400) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      rejected?: ModelCatalogRejection[];
    } | null;
    if (body?.rejected?.length) {
      throw new ModelCatalogRejectionError(
        body.error ?? 'One or more model ids are not in the allowed catalog.',
        body.rejected,
      );
    }
  }

  if (!response.ok) {
    throw new Error(`Admin config request failed: HTTP ${response.status} ${path}`);
  }

  return response.json() as Promise<AIConfig>;
}
